/**
 * Coruscate Creative Studio Admin Portal - API Client
 * Integrates with Cloudflare Workers API backend
 */

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8787';

// Types based on backend API responses
export interface User {
  id: string;
  email: string;
  role: 'admin' | 'editor';
  isActive: boolean;
  status: 'pending' | 'active' | 'inactive';
  lastLogin?: string;
  createdAt: string;
}

export interface Section {
  id: string;
  title: string;
  slug: string;
  shortDescription?: string;
  longDescription?: string;
  isPublished: boolean;
  isVideo: boolean;
  viewCount: number;
  sortOrder: number;
  featuredImage?: {
    url: string;
    alt?: string;
  };
  featuredImageId?: string; // Add this field
  images?: Image[];
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface FeaturedSection {
  id: string;
  sectionId: string;
  sequence: number;
  isActive: boolean;
  title: string;
  shortDescription?: string;
  featuredImageUrl?: string;
  slug: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Image {
  id: string;
  filename: string;
  url: string;
  thumbnailUrl?: string;
  title?: string;
  description?: string;
  alt?: string;
  sortOrder: number;
  sectionId?: string;
  width?: number;
  height?: number;
}

export interface Homepage {
  id: string;
  heroTitle: string;
  heroSubtitle: string;
  heroImageUrl?: string;
  heroVideoUrl?: string;
  navigationText: string;
  updatedAt: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  token: string;
  user: User;
  expiresIn: string;
}

export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  message?: string;
  success?: boolean;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// API Client Class
class ApiClient {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.token = localStorage.getItem('admin_token');
  }

  // Set authentication token
  setToken(token: string) {
    this.token = token;
    localStorage.setItem('admin_token', token);
  }

  // Remove authentication token
  clearToken() {
    this.token = null;
    localStorage.removeItem('admin_token');
  }

  // Make authenticated request
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    // Add authorization header if token exists
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  // Authentication
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    console.log('🔐 Login attempt started for:', credentials.email);
    
    console.log('🌐 Attempting API login...');
    const response = await this.request<AuthResponse>('/api/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    console.log('✅ API login successful');
    return response.data || response as AuthResponse;
  }

  async logout(): Promise<void> {
    await this.request('/api/admin/auth/logout', {
      method: 'POST',
    });
    this.clearToken();
  }

  async refreshToken(): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/api/admin/auth/refresh', {
      method: 'POST',
    });
    return response.data || response as AuthResponse;
  }

  async getUserProfile(): Promise<User> {
    const response = await this.request<User>('/api/admin/auth/me');
    return response.data || response as User;
  }

  // Sections Management
  async getSections(params: {
    page?: number;
    limit?: number;
    search?: string;
    published?: boolean;
  } = {}): Promise<ApiResponse<Section[]>> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });

    return this.request<Section[]>(`/api/admin/sections?${searchParams}`);
  }

  async getSection(id: string): Promise<Section> {
    const response = await this.request<Section>(`/api/admin/sections/${id}`);
    return response.data || response as Section;
  }

  async createSection(section: Partial<Section>): Promise<{ success: boolean; id: string; slug: string }> {
    const response = await this.request<{ success: boolean; id: string; slug: string }>('/api/admin/sections', {
      method: 'POST',
      body: JSON.stringify(section),
    });
    return response.data || response as { success: boolean; id: string; slug: string };
  }

  async updateSection(id: string, section: Partial<Section>): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean }>(`/api/admin/sections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(section),
    });
    return response.data || response as { success: boolean };
  }

  async deleteSection(id: string): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean }>(`/api/admin/sections/${id}`, {
      method: 'DELETE',
    });
    return response.data || response as { success: boolean };
  }

  async toggleSectionPublish(id: string, isPublished: boolean): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean }>(`/api/admin/sections/${id}/publish`, {
      method: 'PATCH',
      body: JSON.stringify({ isPublished }),
    });
    return response.data || response as { success: boolean };
  }

  async getImages(params: { 
    sectionId?: string; 
    page?: number; 
    limit?: number; 
  } = {}): Promise<ApiResponse<Image[]>> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });

    return this.request<Image[]>(`/api/admin/images?${searchParams}`);
  }

  async uploadImages(
    sectionId: string, 
    files: File[], 
    metadata: { 
      titles?: string[]; 
      descriptions?: string[]; 
      altTexts?: string[];
      widths?: (number | null)[];
      heights?: (number | null)[];
    } = {}
  ): Promise<{ success: boolean; data: Image[] }> {
    const formData = new FormData();
    
    // Add section ID
    formData.append('sectionId', sectionId);
    
    // Add files
    files.forEach((file, index) => {
      formData.append(`images[${index}]`, file);
    });
    
    // Add metadata
    metadata.titles?.forEach((title, index) => {
      formData.append(`titles[${index}]`, title);
    });
    
    metadata.descriptions?.forEach((description, index) => {
      formData.append(`descriptions[${index}]`, description);
    });
    
    metadata.altTexts?.forEach((altText, index) => {
      formData.append(`altTexts[${index}]`, altText);
    });

    // Add dimensions
    metadata.widths?.forEach((width, index) => {
      if (width !== null && width !== undefined) {
        formData.append(`widths[${index}]`, width.toString());
      }
    });

    metadata.heights?.forEach((height, index) => {
      if (height !== null && height !== undefined) {
        formData.append(`heights[${index}]`, height.toString());
      }
    });

    const url = `${this.baseURL}/api/admin/images/upload`;
    const headers: Record<string, string> = {};

    // Add authorization header if token exists
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Image upload failed:', error);
      throw error;
    }
  }

  async deleteImage(id: string): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean }>(`/api/admin/images/${id}`, {
      method: 'DELETE',
    });
    return response.data || response as { success: boolean };
  }

  async reorderImages(sectionId: string, imageIds: string[]): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean }>('/api/admin/images/reorder', {
      method: 'POST',
      body: JSON.stringify({ sectionId, imageIds }),
    });
    return response.data || response as { success: boolean };
  }

  async updateImage(id: string, image: Partial<Image>): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean }>(`/api/admin/images/${id}`, {
      method: 'PUT',
      body: JSON.stringify(image),
    });
    return response.data || response as { success: boolean };
  }

  async setFeaturedImage(sectionId: string, imageId: string | null): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean }>('/api/admin/sections/featured-image', {
      method: 'PUT',
      body: JSON.stringify({ sectionId, imageId }),
    });
    return response.data || response as { success: boolean };
  }

  // Homepage Management
  async getHomepage(): Promise<Homepage> {
    const response = await this.request<Homepage>('/api/admin/homepage');
    return response.data || response as Homepage;
  }

  async updateHomepage(homepage: Partial<Homepage>): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean }>('/api/admin/homepage', {
      method: 'PUT',
      body: JSON.stringify(homepage),
    });
    return response.data || response as { success: boolean };
  }

  // Featured Sections Management
  async getFeaturedSections(): Promise<ApiResponse<FeaturedSection[]>> {
    return this.request<FeaturedSection[]>('/api/admin/featured-sections');
  }

  async addFeaturedSection(sectionId: string): Promise<{ success: boolean; sequence?: number }> {
    const response = await this.request<{ success: boolean; sequence?: number }>('/api/admin/featured-sections', {
      method: 'POST',
      body: JSON.stringify({ sectionId }),
    });
    return response.data || response as { success: boolean; sequence?: number };
  }

  async removeFeaturedSection(sectionId: string): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean }>(`/api/admin/featured-sections/${sectionId}`, {
      method: 'DELETE',
    });
    return response.data || response as { success: boolean };
  }

  async reorderFeaturedSections(sectionIds: string[]): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean }>('/api/admin/featured-sections/reorder', {
      method: 'PUT',
      body: JSON.stringify({ sectionIds }),
    });
    return response.data || response as { success: boolean };
  }

  // User Management (Admin only)
  async getUsers(): Promise<ApiResponse<User[]>> {
    return this.request<User[]>('/api/admin/users');
  }

  async inviteUser(email: string, role: 'admin' | 'editor'): Promise<{ success: boolean; invitationLink?: string; userId?: string; user?: { tempPassword: string; email: string; role: string; id: string; status: string } }> {
    const response = await this.request<{ success: boolean; invitationLink?: string; userId?: string; user?: { tempPassword: string; email: string; role: string; id: string; status: string } }>('/api/admin/users/invite', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
    return response.data || response as { success: boolean; invitationLink?: string; userId?: string; user?: { tempPassword: string; email: string; role: string; id: string; status: string } };
  }

  async updateUser(id: string, updates: { role?: 'admin' | 'editor'; isActive?: boolean; status?: 'pending' | 'active' | 'inactive' }): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean }>(`/api/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return response.data || response as { success: boolean };
  }

  async deleteUser(id: string): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean }>(`/api/admin/users/${id}`, {
      method: 'DELETE',
    });
    return response.data || response as { success: boolean };
  }

  async resendInvitation(id: string): Promise<{ success: boolean; invitationLink?: string }> {
    const response = await this.request<{ success: boolean; invitationLink?: string }>(`/api/admin/users/${id}/resend-invitation`, {
      method: 'POST',
    });
    return response.data || response as { success: boolean; invitationLink?: string };
  }

  async setupUser(token: string, password: string): Promise<{ success: boolean }> {
    const response = await this.request<{ success: boolean }>('/api/admin/users/setup', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
    return response.data || response as { success: boolean };
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message?: string }> {
    const response = await this.request<{ success: boolean; message?: string }>('/api/admin/users/change-password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    return response.data || response as { success: boolean; message?: string };
  }
}

// Export singleton instance
export const apiClient = new ApiClient(API_BASE_URL);

// Export default
export default apiClient;